import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type AgentRole = 'user' | 'model';

export interface AgentContentPart {
  text: string;
}

export interface AgentContent {
  role: AgentRole;
  parts: AgentContentPart[];
}

@Injectable({
  providedIn: 'root',
})
export class GeminiAgentHistoryService {
  private readonly storageKey = 'gemini-agent-history';
  private readonly historySubject: BehaviorSubject<AgentContent[]>;
  readonly history$: Observable<AgentContent[]>;

  constructor() {
    const storedHistory = this.loadHistory();
    this.historySubject = new BehaviorSubject<AgentContent[]>(storedHistory);
    this.history$ = this.historySubject.asObservable();
  }

  addUserMessage(message: string): void {
    this.pushMessage('user', message);
  }

  addModelMessage(message: string): void {
    this.pushMessage('model', message);
  }

  getHistorySnapshot(): AgentContent[] {
    return [...this.historySubject.value];
  }

  setHistory(history: AgentContent[]): void {
    this.historySubject.next(history);
    this.persist(history);
  }

  clear(): void {
    this.setHistory([]);
  }

  private pushMessage(role: AgentRole, text: string): void {
    const updatedHistory = [
      ...this.historySubject.value,
      {
        role,
        parts: [{ text }],
      },
    ];
    this.setHistory(updatedHistory);
  }

  private loadHistory(): AgentContent[] {
    try {
      const raw = localStorage.getItem(this.storageKey);
      if (!raw) {
        return [];
      }
      const parsed = JSON.parse(raw) as AgentContent[];
      if (!Array.isArray(parsed)) {
        return [];
      }
      return parsed;
    } catch {
      return [];
    }
  }

  private persist(history: AgentContent[]): void {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(history));
    } catch {
      // If localStorage is unavailable, we silently ignore persistence errors.
    }
  }
}
